import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  output,
  signal,
} from '@angular/core';

import { Dynamic } from '@shared/registry/dynamic';

import { type StepListConfig, type StepStatus } from './step-list.types';

const DEFAULT_INTERVAL = 1000;

@Component({
  selector: 'app-step-list',
  templateUrl: './step-list.html',
  imports: [Dynamic],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StepList {
  private readonly destroyRef = inject(DestroyRef);

  readonly config = input.required<StepListConfig>();
  readonly completed = output<void>();

  private readonly completedCount = signal(0);
  private timerId: ReturnType<typeof setTimeout> | null = null;

  protected readonly stepConfigs = computed(() => {
    const steps = this.config().steps;
    const done = this.completedCount();

    return steps.map((step, index) => {
      const status: StepStatus = index < done ? 'completed' : 'loading';

      return {
        text: step.text,
        status,
        textConfig: {
          text: step.text,
          size: 'sm' as const,
          fontWeight: 'bold' as const,
          cssVarPrefix: 'steplist',
        },
      };
    });
  });

  constructor() {
    afterNextRender(() => {
      this.scheduleNext();
    });

    this.destroyRef.onDestroy(() => {
      this.clearTimer();
    });
  }

  private scheduleNext(): void {
    const steps = this.config().steps;
    const interval = this.config().interval ?? DEFAULT_INTERVAL;

    if (this.completedCount() >= steps.length) {
      this.completed.emit();
      return;
    }

    this.timerId = setTimeout(() => {
      this.completedCount.update((count) => count + 1);
      this.scheduleNext();
    }, interval);
  }

  private clearTimer(): void {
    if (this.timerId) {
      clearTimeout(this.timerId);
      this.timerId = null;
    }
  }
}
